import moment from "moment";

export type MeetingRoom = {
  id: number | string;
  name: string;
  location?: string | null;
  capacity?: number | string | null;
  color?: string | null;
  description?: string | null;
  id_img?: number | string | null;
  url_img?: { path?: string | null; url?: string | null } | string | null;
  active?: boolean;
  removed?: boolean;
};

export type MeetingRoomConfig = {
  value: string;
  label: string;
  shortLabel: string;
  color: string;
  dotClass: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
};

export const DEFAULT_MEETING_DURATION_MINUTES = 60;

export const DEFAULT_ROOM_COLORS = ["#153DCE", "#54D62C", "#F0BA0A", "#12A4A6", "#7C3AED", "#E11D48"];

export const MEETING_ROOMS: MeetingRoomConfig[] = [
  {
    value: "meeting_side_1",
    label: "Meeting Side 1",
    shortLabel: "Side 1",
    color: "#153DCE",
    dotClass: "bg-[#153DCE]",
    bgClass: "bg-[#153DCE]/10",
    textClass: "text-[#153DCE]",
    borderClass: "border-[#153DCE]/20",
  },
  {
    value: "meeting_side_2",
    label: "Meeting Side 2",
    shortLabel: "Side 2",
    color: "#54D62C",
    dotClass: "bg-[#54D62C]",
    bgClass: "bg-[#54D62C]/15",
    textClass: "text-[#238A12]",
    borderClass: "border-[#54D62C]/25",
  },
  {
    value: "meeting_side_3",
    label: "Meeting Side 3",
    shortLabel: "Side 3",
    color: "#F0BA0A",
    dotClass: "bg-[#F0BA0A]",
    bgClass: "bg-[#F0BA0A]/15",
    textClass: "text-[#B47F00]",
    borderClass: "border-[#F0BA0A]/25",
  },
];

type MeetingLike = {
  date?: string | Date | null;
  end_date?: string | Date | null;
  endDate?: string | Date | null;
  meeting_room?: string | null;
  meeting_room_id?: number | string | null;
  meetingRoom?: MeetingRoom | null;
};

function fallbackColor(room?: Partial<MeetingRoom> | string | null) {
  if (typeof room === "object" && room?.color) return room.color;
  const id = typeof room === "object" ? Number(room?.id) : NaN;
  if (Number.isFinite(id)) {
    return DEFAULT_ROOM_COLORS[(id - 1) % DEFAULT_ROOM_COLORS.length];
  }
  const legacy = MEETING_ROOMS.find((item) => item.value === room || item.label === room);
  return legacy?.color ?? DEFAULT_ROOM_COLORS[0];
}

function readableRoomName(value?: string | null) {
  const legacy = MEETING_ROOMS.find((item) => item.value === value || item.label === value);
  return legacy?.label ?? value ?? null;
}

export function resolveMeetingRoom(input?: MeetingLike | MeetingRoom | string | null) {
  if (!input) return null;
  if (typeof input === "string") {
    const label = readableRoomName(input);
    return label
      ? ({
          id: input,
          name: label,
          color: fallbackColor(input),
        } as MeetingRoom)
      : null;
  }
  if ("meetingRoom" in input && input.meetingRoom) return input.meetingRoom;
  if ("name" in input && input.name) return input as MeetingRoom;
  if ("meeting_room" in input && input.meeting_room) {
    return {
      id: input.meeting_room_id ?? input.meeting_room,
      name: readableRoomName(input.meeting_room) ?? input.meeting_room,
      color: fallbackColor(input.meeting_room),
    } as MeetingRoom;
  }
  return null;
}

export function getMeetingRoomConfig(input?: MeetingLike | MeetingRoom | string | null) {
  const room = resolveMeetingRoom(input);
  if (!room) return null;
  const color = fallbackColor(room);
  const shortLabel = room.name.replace(/^Meeting\s+/i, "").trim() || room.name;

  return {
    value: String(room.id),
    label: room.name,
    shortLabel,
    color,
    dotClass: "",
    bgClass: "",
    textClass: "",
    borderClass: "",
  };
}

export function getMeetingRoomLabel(input?: MeetingLike | MeetingRoom | string | null) {
  return getMeetingRoomConfig(input)?.label ?? null;
}

export function getRoomColor(input?: MeetingLike | MeetingRoom | string | null) {
  return getMeetingRoomConfig(input)?.color ?? DEFAULT_ROOM_COLORS[0];
}

export function getMeetingRoomImage(input?: MeetingLike | MeetingRoom | string | null) {
  const room = resolveMeetingRoom(input);
  const image = room?.url_img;
  if (!image) return null;
  if (typeof image === "string") return image;
  return image.path ?? image.url ?? null;
}

export function roomMatchesReunion(room: MeetingRoom, reunion: MeetingLike) {
  if (reunion?.meeting_room_id && String(reunion.meeting_room_id) === String(room.id)) return true;
  if (reunion?.meetingRoom?.id && String(reunion.meetingRoom.id) === String(room.id)) return true;
  return !!reunion?.meeting_room && readableRoomName(reunion.meeting_room) === room.name;
}

export function getMeetingEndDate(reunion: MeetingLike) {
  if (reunion?.end_date) return reunion.end_date;
  if (reunion?.endDate) return reunion.endDate;
  return moment.utc(reunion?.date).add(DEFAULT_MEETING_DURATION_MINUTES, "minutes").toISOString();
}

export function formatMeetingTimeRange(reunion: MeetingLike) {
  if (!reunion?.date) return "-";
  return `${moment.utc(reunion.date).format("HH:mm")} - ${moment
    .utc(getMeetingEndDate(reunion))
    .format("HH:mm")}`;
}

export function buildMeetingRoomOption(input?: MeetingLike | MeetingRoom | string | null) {
  const room = resolveMeetingRoom(input);
  return room ? { value: room.id, label: room.name, data: room } : null;
}
